import { z } from "zod";

const httpUrlSchema = z
  .string()
  .trim()
  .url("Reference URL must be a valid URL.")
  .refine((value) => value.startsWith("https://") || value.startsWith("http://"), {
    message: "Reference URL must start with https:// or http://",
  });

const optionalHttpUrlSchema = z
  .string()
  .trim()
  .optional()
  .transform((value) => value ?? "")
  .refine((value) => value.length === 0 || value.startsWith("https://") || value.startsWith("http://"), {
    message: "Reference URL must start with https:// or http://",
  })
  .refine((value) => value.length === 0 || z.string().url().safeParse(value).success, {
    message: "Reference URL must be a valid URL.",
  });

export const wikipediaUrlSchema = z
  .string()
  .trim()
  .regex(
    /^https:\/\/en\.wikipedia\.org\/wiki\/[a-zA-Z0-9_().'%,-]+$/,
    "Invalid Wikipedia link. Example: https://en.wikipedia.org/wiki/Crime",
  );

export const impactCreateSchema = z
  .object({
    showLink: z.boolean(),
    showTitle: z.boolean(),
    referenceLink: optionalHttpUrlSchema,
    title: z.string().max(300, "Title must be 300 characters or fewer."),
    description: z.string(),
    mediaCount: z.number().int().min(0),
  })
  .superRefine((value, ctx) => {
    const hasLink = value.showLink && value.referenceLink.length > 0;
    const hasTitleContent = value.showTitle && (value.title.trim().length > 0 || value.description.trim().length > 0);
    const hasMedia = value.mediaCount > 0;
    if (!hasLink && !hasTitleContent && !hasMedia) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Media, a reference link, or title/content are required.",
      });
    }
  });

const actionPlaceSchema = z
  .object({
    is_virtual: z.boolean(),
    location: z.object({
      event_url: z.string().trim().optional(),
      address: z.string().trim().optional(),
      latitude: z.number().optional(),
      longitude: z.number().optional(),
      place_name: z.string().optional(),
    }),
    note: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.is_virtual && !value.location.event_url?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Virtual locations require an event URL." });
    }
    if (!value.is_virtual && !value.location.address?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Offline locations require an address." });
    }
  });

export const actionCreateSchema = z
  .object({
    showTextField: z.boolean(),
    title: z.string().max(300, "Title must be 300 characters or fewer."),
    mediaCount: z.number().int().min(0),
    startAt: z.date().nullable(),
    endAt: z.date().nullable(),
    places: z.array(actionPlaceSchema),
    hasIssue: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const hasTitle = value.showTextField && value.title.trim().length > 0;
    const hasMedia = value.mediaCount > 0;
    if (!hasTitle && !hasMedia) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "At least a title or media is required." });
    }
    if (!value.hasIssue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Issue is required." });
    }
    if (value.startAt && value.endAt && value.endAt < value.startAt) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "End time must be after start time." });
    }
  });

export const solutionCreateSchema = z
  .object({
    showLinkField: z.boolean(),
    showTitleField: z.boolean(),
    referenceLink: optionalHttpUrlSchema,
    name: z.string().max(300, "Title must be 300 characters or fewer."),
    description: z.string(),
    mediaCount: z.number().int().min(0),
    hasIssue: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const hasValidLink = value.showLinkField && value.referenceLink.length > 0;
    const hasMedia = value.mediaCount > 0;
    const hasText = value.showTitleField && (value.name.trim().length > 0 || value.description.trim().length > 0);
    if (!hasValidLink && !hasMedia && !hasText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Add a link, media, or text content before submitting.",
      });
    }
    if (!value.hasIssue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Issue is required." });
    }
  });

export const collectionCreateSchema = z.object({
  showLinkField: z.boolean(),
  referenceLink: optionalHttpUrlSchema,
  title: z
    .string()
    .trim()
    .min(1, "Collection title is required.")
    .max(300, "Title must be 300 characters or fewer."),
  description: z.string(),
  mediaCount: z.number().int().min(0),
  hasIssue: z.boolean().optional(),
});

/**
 * Edit-mode schema: title is optional because drafts can live for a while
 * before a user commits to a name (graph collections especially — the
 * canvas is the primary artifact; the title is the last thing you tend to
 * pick). Publish-time enforcement lives on the Publish button in the
 * calling surface, not in this schema.
 */
export const collectionEditSchema = z.object({
  showLinkField: z.boolean(),
  referenceLink: optionalHttpUrlSchema,
  title: z
    .string()
    .trim()
    .max(300, "Title must be 300 characters or fewer."),
  description: z.string(),
  mediaCount: z.number().int().min(0),
  hasIssue: z.boolean().optional(),
});

export const teabateCreateSchema = z.object({
  title: z.string().trim().min(1, "Please enter a statement for your teabate").max(300, "Claim must be 300 characters or fewer."),
  hasIssue: z.boolean().refine((value) => value, { message: "Please select an issue for your teabate" }),
});

export const feedbackCategorySchema = z.enum([
  "Bug",
  "Enhancement",
  "Functionality",
  "User Interface (UI)",
  "User Experience (UX)",
  "Performance",
  "Compliance",
  "Company",
  "Other",
]);

/**
 * Feedback creation payload. `category` is optional — the UI exposes it as an
 * optional tag and the backend tolerates missing/empty values. We still reject
 * unknown non-empty strings so callers cannot inject arbitrary labels.
 */
export const feedbackCreateSchema = z
  .object({
    category: z
      .union([feedbackCategorySchema, z.literal("")])
      .optional(),
    showTextField: z.boolean(),
    showLinkField: z.boolean(),
    title: z.string().max(300, "Title must be 300 characters or fewer."),
    description: z.string(),
    referenceLink: optionalHttpUrlSchema,
    mediaCount: z.number().int().min(0),
  })
  .superRefine((value, ctx) => {
    const hasLink = value.showLinkField && value.referenceLink.length > 0;
    const hasMedia = value.mediaCount > 0;
    const hasText = value.showTextField && (value.title.trim().length > 0 || value.description.trim().length > 0);
    if (!hasLink && !hasMedia && !hasText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Feedback requires media, a reference link, or text content.",
      });
    }
  });

export const addIssueSubmissionSchema = z
  .object({
    influencerCname: z.string().trim().min(1, "Please select an influencer issue."),
    influencedCname: z.string().trim().optional(),
    showInfluencerWikipediaField: z.boolean(),
    showInfluencedWikipediaField: z.boolean(),
    wikipediaLink: z.string().optional(),
    topicWikipediaLink: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.showInfluencerWikipediaField) {
      const result = wikipediaUrlSchema.safeParse(value.wikipediaLink ?? "");
      if (!result.success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error.issues[0]?.message ?? "Invalid influencer Wikipedia URL." });
      }
    }
    if (value.showInfluencedWikipediaField && value.topicWikipediaLink?.trim()) {
      const result = wikipediaUrlSchema.safeParse(value.topicWikipediaLink);
      if (!result.success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error.issues[0]?.message ?? "Invalid influenced Wikipedia URL." });
      }
    }
    if (value.influencedCname && value.influencedCname === value.influencerCname) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The influencer and influenced cannot be the same.",
      });
    }
  });

export { httpUrlSchema };
